import api from '.';
import {
  Account,
  AccountSummary,
  AdminAddInviteKeys,
  AdminInviteMemberRequest,
  AddInviteKeys,
  InviteMemberRequest,
  InviteOrganizationAdminRequest,
  Organization,
  OrganizationType,
  PaginatedOrganizations,
  paginationQueryParams,
  PaginationParams,
  VerificationConfiguration,
} from '@karehero/models';

export const organizationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllOrganization: builder.query<
      PaginatedOrganizations,
      PaginationParams | void
    >({
      query: (params) => ({
        url: '/organization',
        params: paginationQueryParams(params),
      }),
      providesTags: ['Organizations'],
    }),
    getOrganization: builder.query<Organization, string>({
      query: (id) => `/organization/${id}`,
      providesTags: ['Organizations'],
    }),
    getVerificationConfigurationForOrganization: builder.query<
      VerificationConfiguration,
      string
    >({
      query: (id) => `/organization/${id}/verification-config`,
      providesTags: ['Organizations'],
    }),
    getSessionOrganization: builder.query<Organization, void>({
      query: () => `/organization/session`,
      providesTags: ['Organizations'],
    }),
    createOrganization: builder.mutation<Organization, Organization>({
      query: (organization) => ({
        url: '/organization',
        method: 'POST',
        body: organization,
      }),
      invalidatesTags: ['Organizations'],
    }),
    updateOrganization: builder.mutation<Organization, Organization>({
      query: (organization) => ({
        url: `/organization`,
        method: 'PUT',
        body: organization,
      }),
      invalidatesTags: ['Organizations', 'ReferringPartners'],
    }),
    getOrganizationAccounts: builder.query<
      AccountSummary[],
      { organizationID: string; role?: string }
    >({
      query: ({ organizationID, role }) => {
        const params = role ? `?role=${role}` : '';
        return `/organization/${organizationID}/account${params}`;
      },
      providesTags: ['OrganizationMembers'],
    }),
    inviteMembersToOrganization: builder.mutation<void, InviteMemberRequest>({
      query: (req) => ({
        url: `/organization/invite`,
        method: 'POST',
        body: req,
      }),
      invalidatesTags: ['Organizations', 'OrganizationMembers'],
    }),
    inviteOrganizationAdmin: builder.mutation<
      void,
      InviteOrganizationAdminRequest
    >({
      query: (req) => ({
        url: `/organization/admin/invite`,
        method: 'POST',
        body: req,
      }),
      invalidatesTags: ['Organizations', 'OrganizationMembers'],
    }),
    reinviteMember: builder.mutation<void, string>({
      query: (id) => ({
        url: `/invite/${id}/resend`,
        method: 'POST',
      }),
      invalidatesTags: [
        'Organizations',
        'OrganizationMembers',
        'InvitedMembers',
      ],
    }),
    deleteInvite: builder.mutation<void, string>({
      query: (id) => ({
        url: `/invite/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        'Organizations',
        'OrganizationMembers',
        'InvitedMembers',
      ],
    }),
    resendEmailInvitesToAllMembers: builder.mutation<void, string>({
      query: (organizationId) => ({
        url: `/organization/${organizationId}/invites/send`,
        method: 'POST',
      }),
      invalidatesTags: ['Organizations', 'OrganizationMembers'],
    }),
    addInviteKeysToOrganization: builder.mutation<void, AddInviteKeys>({
      query: (req) => ({
        url: `/organization/invite-keys`,
        method: 'POST',
        body: req,
      }),
      invalidatesTags: ['Organizations', 'OrganizationMembers'],
    }),
    inviteMembersToOrganizationAdmin: builder.mutation<
      void,
      AdminInviteMemberRequest
    >({
      query: (req) => ({
        url: `/organization/${req.organizationID}/invite`,
        method: 'POST',
        body: req,
      }),
      invalidatesTags: ['Organizations', 'OrganizationMembers'],
    }),
    addInviteKeysToOrganizationAdmin: builder.mutation<
      void,
      AdminAddInviteKeys
    >({
      query: (req) => ({
        url: `/organization/${req.organizationID}/invite-keys`,
        method: 'POST',
        body: req,
      }),
      invalidatesTags: ['Organizations', 'OrganizationMembers'],
    }),
    getAllOrganizationType: builder.query<OrganizationType[], void>({
      query: () => `/organization/type`,
    }),
    getInviteEmailTemplate: builder.query<string, void>({
      query: () => {
        return {
          url: `/organization/invite-email-template`,
          method: 'GET',
          responseHandler: 'text',
        };
      },
    }),
    createReferringOrganization: builder.mutation<
      Account,
      {
        firstName: string;
        lastName: string;
        partnerCode?: string;
        referLtcCode?: string;
        phoneNumber?: string;
        organisationCreationCode?: string;
      }
    >({
      query: (body) => ({
        url: '/organization/referrer/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Organizations'],
    }),
  }),
});

export const {
  useGetAllOrganizationQuery,
  useGetVerificationConfigurationForOrganizationQuery,
  useGetSessionOrganizationQuery,
  useGetOrganizationQuery,
  useLazyGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useGetOrganizationAccountsQuery,
  useInviteMembersToOrganizationMutation,
  useInviteMembersToOrganizationAdminMutation,
  useAddInviteKeysToOrganizationMutation,
  useAddInviteKeysToOrganizationAdminMutation,
  useGetAllOrganizationTypeQuery,
  useGetInviteEmailTemplateQuery,
  useReinviteMemberMutation,
  useDeleteInviteMutation,
  useResendEmailInvitesToAllMembersMutation,
  useCreateReferringOrganizationMutation,
  useInviteOrganizationAdminMutation,
} = organizationApi;

export default organizationApi;
